import { type EventVisibility, type State } from "./schema/v1/v1";

/**
 * The two event fields every visibility decision reads: `event_id` keys the
 * per-event overrides, `name` is what a `named` rule lists.
 */
export interface EventVisibilitySubject {
  event_id: string;
  name: string;
}

/**
 * Builds the single per-event visibility decision, shared by every surface
 * that draws events (timeline track, plot annotations, image panel pills,
 * sidebar rows) and by the reducer's diff-aware override writes:
 *
 * 1. An explicit per-event override (`State.events`) wins — the user clicked.
 * 2. Otherwise the workspace rule (`State.settings.events`).
 * 3. Otherwise visible.
 *
 * The rule compiles once here rather than per event, so build one resolver
 * and apply it across a list instead of calling this per event.
 */
export function makeEventVisibilityResolver(
  eventConfigs: State["events"],
  rule: EventVisibility | undefined,
): (event: EventVisibilitySubject) => boolean {
  const ruleAllows = compileRule(rule);

  return function isEventVisible(event) {
    return eventConfigs?.[event.event_id]?.isVisible ?? ruleAllows(event.name);
  };
}

/**
 * Reduces a rule to a name predicate — the rule's verdict *before* per-event
 * overrides. The reducer uses this to write overrides only where a request
 * disagrees with the rule, and to clear them where it agrees.
 *
 * Fail-open throughout: no rule, an empty `named` list (an unfinished rule
 * must never blank the event track on a link the viewer cannot edit), a
 * malformed variant, and a mode outside the union (a newer client's rule, or
 * a writer this module cannot see) all show everything.
 */
export function compileRule(
  rule: EventVisibility | undefined,
): (name: string) => boolean {
  if (rule === undefined) {
    return () => true;
  }

  switch (rule.mode) {
    case "all":
      return () => true;
    case "none":
      return () => false;
    case "named": {
      if (!Array.isArray(rule.names) || rule.names.length === 0) {
        return () => true;
      }
      const names = new Set(rule.names);
      return (name) => names.has(name);
    }
    default:
      return () => true;
  }
}
